import React, { Dispatch, SetStateAction, useEffect, useState, forwardRef } from 'react'
import useSearchQuery from '../../../common/hooks/useSearchQuery'
import { useNavigate } from 'react-router-dom'
import useUrlQuery from '../../../common/hooks/useUrlQuery'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import toast from 'react-hot-toast'
import AutoCompleteResponse from '../../../common/response/AutoCompleteResponse'
import SearchHistoryResponse from '../../../common/response/SearchHistoryResponse'
import useLoginStore from '../../login/useLoginStore'
import searchCache from './searchCache'
import SearchBar, { SearchBarHandle } from './SearchBar'

interface Props {
	autoCompleteApiUrl: string | null
	searchHistoryApiUrl: string | null
	searchResultUrl?: string
	searchHistoryCacheKey: string | null
	placeholder: string
	isHandleSubmit: boolean
	isShouldUpdateQueryParam: boolean
	handleQuerySuggestionClick?: (suggestion: AutoCompleteResponse) => void
	addFirstItemOnSubmit?: (item: any) => void
	autoCompleteTimeout?: number
	mapSearchHistoryResponse?: (data: any[]) => SearchHistoryResponse[]
}

const SearchBarWithHistoryAndSuggestion = forwardRef<SearchBarHandle, Props>(({ autoCompleteApiUrl, searchHistoryApiUrl, searchResultUrl, searchHistoryCacheKey, placeholder, isHandleSubmit, isShouldUpdateQueryParam, handleQuerySuggestionClick, addFirstItemOnSubmit, autoCompleteTimeout, mapSearchHistoryResponse }, ref) => {
	const navigate = useNavigate()
	const isLoggedIn = useLoginStore(state => state.isLoggedIn())

	const defaultQuery = useUrlQuery('q') ?? ''
	const { query, setQuery, dQuery } = useSearchQuery(defaultQuery, autoCompleteTimeout ?? 200)

	const [querySuggestions, setQuerySuggestions] = useState<AutoCompleteResponse[]>([])
	const [isShowingQuerySuggestions, setIsShowingQuerySuggestions] = useState<boolean>(false)

	const [queryHistories, setQueryHistories] = useState<SearchHistoryResponse[]>([])
	const [isShowingQueryHistory, setIsShowingQueryHistory] = useState<boolean>(false)

	useEffect(() => {
		if (dQuery !== '' && autoCompleteApiUrl != null) {
			const url = autoCompleteApiUrl.includes('?') ? `${autoCompleteApiUrl}&q=${dQuery}` : `${autoCompleteApiUrl}?q=${dQuery}`
			BooksitoutServer.get(url).then(res => {
				if (res.data.length !== 0) {
					setQuerySuggestions(res.data)
				}
			})
		} else {
			setQuerySuggestions([])
		}
	}, [autoCompleteApiUrl, dQuery])

	useEffect(() => {
		if ((searchHistoryCacheKey != null || searchHistoryApiUrl != null) && dQuery === '') {
			if (isLoggedIn && searchHistoryApiUrl != null) {
				BooksitoutServer.get(searchHistoryApiUrl).then(res => {
					const histories = mapSearchHistoryResponse ? mapSearchHistoryResponse(res.data ?? []) : (res.data ?? [])
					setQueryHistories(histories)
					if (searchHistoryCacheKey != null) {
						localStorage.setItem(searchHistoryCacheKey, JSON.stringify(histories))
					}
				})
			} else if (searchHistoryCacheKey != null) {
				const storedHistories = localStorage.getItem(searchHistoryCacheKey)
				if (storedHistories != null) {
					setQueryHistories(JSON.parse(storedHistories) ?? [])
				}
			}
		}
	}, [searchHistoryApiUrl, dQuery, isLoggedIn, searchHistoryCacheKey])

	useEffect(() => {
		;(setQuery as Dispatch<SetStateAction<string>>)(defaultQuery)
	}, [defaultQuery, setQuery])

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>, focus: () => void) => {
		e.preventDefault()
		if (query.length < 2) {
			toast.error('검색어는 2글자 이상이어야 해요')
			focus()
			return
		}

		if (searchHistoryCacheKey != null || searchHistoryCacheKey !== '') {
			searchCache.updateCache(searchHistoryCacheKey, dQuery.toString(), `${searchResultUrl}?q=${query}`)
		}

		if (querySuggestions.length > 0 && querySuggestions[0].url) {
			navigate(querySuggestions[0].url)
			return
		}

		if (isHandleSubmit && searchResultUrl) {
			navigate(`${searchResultUrl}?q=${query}`)
		}
	}

	return <SearchBar ref={ref} placeholder={placeholder} query={query} isShowingQueryHistory={dQuery === '' && isShowingQueryHistory} isShowingAutoComplete={dQuery != null && dQuery !== defaultQuery && isShowingQuerySuggestions} isShouldUpdateQueryParam={isShouldUpdateQueryParam} queryHistories={queryHistories} querySuggestions={querySuggestions} setIsShowingQueryHistory={isShowing => setIsShowingQueryHistory(isShowing)} setIsShowingAutoComplete={isShowing => setIsShowingQuerySuggestions(isShowing)} setQuery={q => setQuery(q)} handleSubmit={handleSubmit} addFirstItemOnSubmit={addFirstItemOnSubmit ?? null} handleQuerySuggestionClick={handleQuerySuggestionClick} />
})

export default SearchBarWithHistoryAndSuggestion
